import { useEffect, useState } from "react";
import { IconX } from "../layout/Icons";

/**
 * Replit-style Notifications popover. Two tabs (Unread / All) with a
 * list of recent notifications + a Load more affordance.
 *
 * State lives in localStorage today — there is no /api/notifications
 * endpoint yet. The hook below is the contract: when the server ships
 * the stream, swap the storage source and the panel keeps working.
 */

export interface AppNotification {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly createdAt: string;
  readonly unread: boolean;
}

const STORAGE_KEY = "vp.notifications.v1";

export function useNotifications() {
  const [items, setItems] = useState<AppNotification[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as AppNotification[];
    } catch {
      // ignore
    }
    return [];
  });

  function persist(next: AppNotification[]) {
    setItems(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent("vp:notifications:changed"));
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    function onChange() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        setItems(raw ? (JSON.parse(raw) as AppNotification[]) : []);
      } catch {
        // ignore
      }
    }
    window.addEventListener("vp:notifications:changed", onChange);
    return () => window.removeEventListener("vp:notifications:changed", onChange);
  }, []);

  return {
    items,
    unreadCount: items.filter((n) => n.unread).length,
    markAllRead: () => persist(items.map((n) => ({ ...n, unread: false }))),
    markRead: (id: string) =>
      persist(items.map((n) => (n.id === id ? { ...n, unread: false } : n))),
  };
}

export function NotificationsPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { items, unreadCount, markAllRead, markRead } = useNotifications();
  const [tab, setTab] = useState<"unread" | "all">("unread");

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const filtered = tab === "unread" ? items.filter((n) => n.unread) : items;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
      data-testid="notifications-panel"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        paddingTop: 80,
        paddingLeft: 260,
        zIndex: 65,
      }}
    >
      <div
        style={{
          width: 360,
          maxHeight: "60vh",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 10,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 16px 50px rgba(0,0,0,0.45)",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "10px 14px",
            borderBottom: "1px solid var(--border-default)",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600 }}>Notifications</span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notifications"
            style={{
              width: 24,
              height: 24,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              color: "var(--text-tertiary)",
              cursor: "pointer",
            }}
          >
            <IconX size={10} />
          </button>
        </header>
        <div
          role="tablist"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            background: "var(--bg-panel-2)",
            borderBottom: "1px solid var(--border-default)",
          }}
        >
          {(["unread", "all"] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              style={{
                padding: "8px 10px",
                background: tab === t ? "var(--bg-panel)" : "transparent",
                border: "none",
                color: tab === t ? "var(--text-primary)" : "var(--text-tertiary)",
                fontSize: 12.5,
                fontWeight: 500,
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {t}
              {t === "unread" && unreadCount > 0 && (
                <span
                  aria-label={`${unreadCount} unread`}
                  style={{
                    marginLeft: 6,
                    padding: "1px 6px",
                    fontSize: 10,
                    background: "var(--accent-red)",
                    color: "#fff",
                    borderRadius: 999,
                    fontWeight: 700,
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {filtered.length === 0 ? (
            <li
              style={{
                padding: "28px 14px",
                color: "var(--text-tertiary)",
                fontSize: 12.5,
                textAlign: "center",
              }}
            >
              {tab === "unread" ? "No unread notifications." : "No notifications yet."}
            </li>
          ) : (
            filtered.map((n) => (
              <li
                key={n.id}
                onClick={() => markRead(n.id)}
                style={{
                  padding: "12px 14px",
                  borderBottom: "1px solid var(--border-default)",
                  cursor: "pointer",
                  background: n.unread ? "var(--bg-panel)" : "transparent",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {n.unread && (
                    <span
                      aria-hidden
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        background: "var(--accent-blue)",
                        display: "inline-block",
                      }}
                    />
                  )}
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{n.title}</span>
                </div>
                <span style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.45 }}>
                  {n.body}
                </span>
                <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  {timeAgo(n.createdAt)}
                </span>
              </li>
            ))
          )}
        </ol>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 14px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel-2)",
          }}
        >
          <button
            type="button"
            onClick={markAllRead}
            disabled={unreadCount === 0}
            style={{
              padding: "4px 10px",
              background: "transparent",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              fontSize: 11,
              color: unreadCount === 0 ? "var(--text-tertiary)" : "var(--text-secondary)",
              cursor: unreadCount === 0 ? "not-allowed" : "pointer",
            }}
          >
            Mark all read
          </button>
          <div style={{ flex: 1 }} />
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}
